import React, { useState, useEffect } from 'react';
import { 
  History, 
  Sparkles, 
  Calendar, 
  Save, 
  Plus, 
  Trash2, 
  Edit3, 
  Clock, 
  Upload, 
  X,
  Tag
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AboutUsHistoryForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    subtitle: '',
    historyItems: [],
    whyStarted: { headline: '', paragraph: '', tags: [], note: '' },
    established: { date: '', facts: [], cta: '' }
  });

  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionIds, setSectionIds] = useState({ history: null, whyStarted: null, established: null });
  const [submitting, setSubmitting] = useState({});
  const [pendingHistoryHeader, setPendingHistoryHeader] = useState({});
  const [pendingHistoryItems, setPendingHistoryItems] = useState({});
  const [pendingWhyStarted, setPendingWhyStarted] = useState(false);
  const [pendingEstablished, setPendingEstablished] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState('history');
  const [editingHistoryIndex, setEditingHistoryIndex] = useState(null);
  const [modalHistoryData, setModalHistoryData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [newTag, setNewTag] = useState('');
  const [newFact, setNewFact] = useState({ label: '', value: '' });
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AboutUsHistoryForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    if (typeof mediaId === 'string' && (mediaId.startsWith('http://') || mediaId.startsWith('https://') || mediaId.startsWith('blob:') || mediaId.startsWith('data:'))) {
      return mediaId;
    }
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    if (mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  const loadData = async () => {
    await Promise.all([loadMediaMap(), loadSections()]);
  };

  const loadSections = async () => {
    setLoading(true);
    try {
      let historySection = null;
      let whyStartedSection = null;
      let establishedSection = null;
      
      const adminResponse = await fetch(
        `${apiBase}/aboutus/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          historySection = result.data.find(s => s.section_type === 'about-us-history-shell');
          whyStartedSection = result.data.find(s => s.section_type === 'about-us-why-started-shell');
          establishedSection = result.data.find(s => s.section_type === 'about-us-established-shell');
        }
      }
      
      if (!historySection || !whyStartedSection || !establishedSection) {
        const publicResponse = await fetch(
          `${apiBase}/aboutus/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            if (!historySection) historySection = result.data.find(s => s.section_type === 'about-us-history-shell');
            if (!whyStartedSection) whyStartedSection = result.data.find(s => s.section_type === 'about-us-why-started-shell');
            if (!establishedSection) establishedSection = result.data.find(s => s.section_type === 'about-us-established-shell');
          }
        }
      }
      
      if (!historySection || !whyStartedSection || !establishedSection) {
        throw new Error('One or more history sections not found');
      }

      setSectionIds({
        history: historySection.id,
        whyStarted: whyStartedSection.id,
        established: establishedSection.id
      });

      const newFormData = {
        kicker: 'Our Journey',
        title: 'Our Beginning & Heritage',
        subtitle: 'A brief story of how and why Permata Harapan School was founded.',
        historyItems: [],
        whyStarted: { headline: '', paragraph: '', tags: [], note: '' },
        established: { date: '', facts: [], cta: '' }
      };

      if (historySection.content_published) {
        let published = historySection.content_published;
        if (typeof published === 'string') {
          try { published = JSON.parse(published); } catch (e) { published = {}; }
        }
        const en = published?.en || {};
        newFormData.kicker = en.kicker || 'Our Journey';
        newFormData.title = en.title || 'Our Beginning & Heritage';
        newFormData.subtitle = en.subtitle || en.intro || 'A brief story of how and why Permata Harapan School was founded.';
        newFormData.historyItems = Array.isArray(en.items) ? [...en.items] : [];
      }

      if (whyStartedSection.content_published) {
        let published = whyStartedSection.content_published;
        if (typeof published === 'string') {
          try { published = JSON.parse(published); } catch (e) { published = {}; }
        }
        const en = published?.en || {};
        newFormData.whyStarted = {
          headline: en.headline || '',
          paragraph: en.paragraph || '',
          tags: Array.isArray(en.tags) ? [...en.tags] : [],
          note: en.note || ''
        };
      }

      if (establishedSection.content_published) {
        let published = establishedSection.content_published;
        if (typeof published === 'string') {
          try { published = JSON.parse(published); } catch (e) { published = {}; }
        }
        const en = published?.en || {};
        newFormData.established = {
          date: en.date || '',
          facts: Array.isArray(en.facts) ? [...en.facts] : [],
          cta: en.cta || ''
        };
      }

      // Check pending for timeline history & header
      const pHistoryMap = {};
      const pHeaderMap = {};
      try {
        const pRes = await fetch(`${apiBase}/cms/section/${historySection.id}/with-pending`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        if (pRes.ok) {
          const pData = await pRes.json();
          if (pData.success && Array.isArray(pData.data?.pending_changes)) {
            pData.data.pending_changes.forEach(ch => {
              if (ch.status === 'pending' && (ch.locale === 'en' || !ch.locale)) {
                const match = ch.field_id.match(/^items\.(\d+)(?:\.|$)/);
                if (match) {
                  const idx = parseInt(match[1], 10);
                  pHistoryMap[idx] = true;
                  let val = ch.new_value;
                  if (typeof val === 'string') {
                    try { val = JSON.parse(val); } catch (e) {}
                  }
                  if (val && newFormData.historyItems[idx]) {
                    newFormData.historyItems[idx] = val;
                  }
                } else if (ch.field_id === 'kicker' || ch.field_id === 'title' || ch.field_id === 'subtitle') {
                  pHeaderMap[ch.field_id] = true;
                  let val = ch.new_value;
                  if (typeof val === 'string') {
                    try { val = JSON.parse(val); } catch (e) {}
                  }
                  if (val !== undefined && val !== null) {
                    newFormData[ch.field_id] = val;
                  }
                }
              }
            });
          }
        }
      } catch (pErr) {
        console.warn('Pending history check error:', pErr);
      }

      // Check pending for whyStarted
      let pWhyStarted = false;
      try {
        const pRes = await fetch(`${apiBase}/cms/section/${whyStartedSection.id}/with-pending`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        if (pRes.ok) {
          const pData = await pRes.json();
          if (pData.success && Array.isArray(pData.data?.pending_changes)) {
            const hasPending = pData.data.pending_changes.some(ch => ch.status === 'pending');
            if (hasPending) {
              pWhyStarted = true;
              const pendingChange = pData.data.pending_changes.find(ch => ch.status === 'pending');
              if (pendingChange?.new_value) {
                let val = pendingChange.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }
                newFormData.whyStarted = { ...newFormData.whyStarted, ...val };
              }
            }
          }
        }
      } catch (pErr) {
        console.warn('Pending whyStarted check error:', pErr);
      }

      // Check pending for established
      let pEstablished = false;
      try {
        const pRes = await fetch(`${apiBase}/cms/section/${establishedSection.id}/with-pending`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        if (pRes.ok) {
          const pData = await pRes.json();
          if (pData.success && Array.isArray(pData.data?.pending_changes)) {
            const hasPending = pData.data.pending_changes.some(ch => ch.status === 'pending');
            if (hasPending) {
              pEstablished = true;
              const pendingChange = pData.data.pending_changes.find(ch => ch.status === 'pending');
              if (pendingChange?.new_value) {
                let val = pendingChange.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }
                newFormData.established = { ...newFormData.established, ...val };
              }
            }
          }
        }
      } catch (pErr) {
        console.warn('Pending established check error:', pErr);
      }

      setFormData(newFormData);
      setOriginalData(newFormData);
      setPendingHistoryHeader(pHeaderMap);
      setPendingHistoryItems(pHistoryMap);
      setPendingWhyStarted(pWhyStarted);
      setPendingEstablished(pEstablished);
    } catch (err) {
      console.error('[AboutUsHistoryForm] Load error:', err);
      toast.error('Error loading history form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmitHeaderField = async (fieldName, label) => {
    if (!sectionIds.history) {
      toast.error('History section ID not loaded');
      return;
    }

    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionIds.history,
          field_id: fieldName,
          field_name: label || fieldName.replace(/_/g, ' '),
          new_value: formData[fieldName],
          old_value: originalData[fieldName],
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`${label || fieldName} submitted for approval`);
        setPendingHistoryHeader(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleAddHistoryItem = () => {
    const newItem = { year: new Date().getFullYear().toString(), title: '', description: '', image: '' };
    const nextIdx = formData.historyItems.length;
    setFormData(prev => ({
      ...prev,
      historyItems: [...prev.historyItems, newItem]
    }));
    setEditingHistoryIndex(nextIdx);
    setModalHistoryData(newItem);
  };

  const handleDeleteHistoryItem = async (itemIndex) => {
    if (pendingHistoryItems[itemIndex]) {
      toast.info('This milestone is awaiting approval');
      return;
    }

    const itemToDelete = formData.historyItems[itemIndex];
    if (!itemToDelete) return;

    if (!window.confirm(`Delete milestone for year ${itemToDelete.year || itemIndex + 1}?`)) return;

    setSubmitting(prev => ({ ...prev, [`hist_del_${itemIndex}`]: true }));
    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionIds.history,
          field_id: `items.${itemIndex}.delete`,
          field_name: `Delete Timeline Item - ${itemToDelete.year || itemIndex + 1}`,
          old_value: itemToDelete,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`Milestone deletion submitted for approval`);
        setPendingHistoryItems(prev => ({ ...prev, [itemIndex]: true }));
      } else {
        toast.error(result.error || 'Delete submission failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`hist_del_${itemIndex}`]: false }));
    }
  };

  const handleHistoryModalChange = (field, value) => {
    setModalHistoryData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              const mediaUrl = response.url || response.file_path || response.data?.url || response.data?.file_path;
              if (mediaId) {
                if (mediaUrl) {
                  setMediaMap(prev => ({
                    ...prev,
                    [mediaId]: mediaUrl,
                    [String(mediaId)]: mediaUrl
                  }));
                }
                handleHistoryModalChange('image', mediaUrl || mediaId);
                handleHistoryModalChange('media_id', mediaId);
                toast.success('Milestone image uploaded! Click Save below to submit for approval.');
              } else {
                toast.error('No media ID returned');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error(`Upload failed (HTTP ${xhr.status})`);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-history-shell`);
      xhr.withCredentials = true;
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (savedToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${savedToken}`);
      }
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleSubmitHistoryItemModal = async () => {
    if (!sectionIds.history) {
      toast.error('Section ID not loaded');
      return;
    }

    const itemIndex = editingHistoryIndex;
    const item = modalHistoryData;
    const itemKey = `item_${itemIndex}`;

    if (!item || !item.year || !item.title || !item.description) {
      toast.warning('Please enter Year, Title, and Description');
      return;
    }

    setSubmitting(prev => ({ ...prev, [itemKey]: true }));

    try {
      const cleanItem = {
        year: (item.year || '').trim(),
        title: (item.title || '').trim(),
        description: (item.description || '').trim(),
        image: item.image || '',
        media_id: item.media_id || ''
      };

      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionIds.history,
            field_id: `items.${itemIndex}.update`,
            field_name: `Update Timeline Milestone - ${cleanItem.year}`,
            old_value: originalData.historyItems[itemIndex] || {},
            new_value: cleanItem,
            locale: 'en'
          })
        }
      );

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`Milestone (${cleanItem.year}) submitted for approval`);
        setPendingHistoryItems(prev => ({ ...prev, [itemIndex]: true }));
        setFormData(prev => ({
          ...prev,
          historyItems: prev.historyItems.map((it, i) => i === itemIndex ? cleanItem : it)
        }));
        setEditingHistoryIndex(null);
        setModalHistoryData(null);
      } else {
        if (response.status === 409) {
          toast.error('This milestone already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  const handleWhyStartedChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      whyStarted: { ...prev.whyStarted, [field]: value }
    }));
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    if ((formData.whyStarted.tags || []).length >= 6) {
      toast.warning('Max 6 tags allowed');
      return;
    }
    setFormData(prev => ({
      ...prev,
      whyStarted: {
        ...prev.whyStarted,
        tags: [...(prev.whyStarted.tags || []), newTag.trim()]
      }
    }));
    setNewTag('');
  };

  const handleRemoveTag = (tagIndex) => {
    setFormData(prev => ({
      ...prev,
      whyStarted: {
        ...prev.whyStarted,
        tags: prev.whyStarted.tags.filter((_, i) => i !== tagIndex)
      }
    }));
  };

  const handleSubmitWhyStarted = async () => {
    if (!sectionIds.whyStarted) {
      toast.error('Section ID not loaded');
      return;
    }

    if (!formData.whyStarted.headline || !formData.whyStarted.paragraph) {
      toast.warning('Please enter both headline and narrative paragraph');
      return;
    }

    setSubmitting(prev => ({ ...prev, 'why-started': true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionIds.whyStarted,
          field_id: 'content.update',
          field_name: 'Update Why We Started',
          old_value: originalData.whyStarted,
          new_value: formData.whyStarted,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success('"Why We Started" narrative submitted for approval');
        setPendingWhyStarted(true);
        setOriginalData(prev => ({ ...prev, whyStarted: formData.whyStarted }));
      } else {
        toast.error(result.error || 'Submit failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, 'why-started': false }));
    }
  };

  const handleEstablishedChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      established: { ...prev.established, [field]: value }
    }));
  };

  const handleAddFact = () => {
    if (!newFact.label.trim() || !newFact.value.trim()) {
      toast.warning('Please enter both label and fact detail');
      return;
    }
    setFormData(prev => ({
      ...prev,
      established: {
        ...prev.established,
        facts: [...prev.established.facts, { label: newFact.label.trim(), value: newFact.value.trim() }]
      }
    }));
    setNewFact({ label: '', value: '' });
  };

  const handleRemoveFact = (index) => {
    setFormData(prev => ({
      ...prev,
      established: { ...prev.established, facts: prev.established.facts.filter((_, i) => i !== index) }
    }));
  };

  const handleSubmitEstablished = async () => {
    if (!sectionIds.established) {
      toast.error('Section ID not loaded');
      return;
    }

    if (!formData.established.date) {
      toast.warning('Please enter the established date');
      return;
    }

    setSubmitting(prev => ({ ...prev, 'established': true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionIds.established,
          field_id: 'content.update',
          field_name: 'Update Established History & Facts',
          old_value: originalData.established,
          new_value: formData.established,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success('Established information submitted for approval');
        setPendingEstablished(true);
        setOriginalData(prev => ({ ...prev, established: formData.established }));
      } else {
        toast.error(result.error || 'Submit failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, 'established': false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading History & Milestones...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <History size={22} />
          </div>
          <div>
            <h2 className="unified-title">School History, Founding & Milestones</h2>
            <p className="unified-subtitle">Manage historical journey timeline, founding narrative, and key institutional milestones</p>
          </div>
        </div>
      </div>

      {/* Section Tag / Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Tag / Kicker</label>
          {pendingHistoryHeader.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="60"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingHistoryHeader.kicker || submitting.kicker}
            placeholder="e.g. Our Journey"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitHeaderField('kicker', 'Section Kicker')}
            disabled={submitting.kicker || pendingHistoryHeader.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Section Headline */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Headline</label>
          {pendingHistoryHeader.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="120"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingHistoryHeader.title || submitting.title}
            placeholder="e.g. Our Beginning & Heritage"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitHeaderField('title', 'Section Headline')}
            disabled={submitting.title || pendingHistoryHeader.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Section Subtitle / Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Subtitle / Description</label>
          {pendingHistoryHeader.subtitle && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength="500"
            className="unified-textarea"
            rows="3"
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={pendingHistoryHeader.subtitle || submitting.subtitle}
            placeholder="e.g. A brief story of how and why Permata Harapan School was founded."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitHeaderField('subtitle', 'Section Subtitle')}
            disabled={submitting.subtitle || pendingHistoryHeader.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} /> {submitting.subtitle ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Internal Sub-Navigation Tabs */}
      <div className="unified-tab-bar" style={{ marginTop: '28px', marginBottom: '24px' }}>
        <button
          type="button"
          className={`tab-pill-btn ${activeSubTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('history')}
        >
          <History size={15} style={{ marginRight: '6px' }} />
          <span>Timeline Milestones ({formData.historyItems.length})</span>
        </button>

        <button
          type="button"
          className={`tab-pill-btn ${activeSubTab === 'whystarted' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('whystarted')}
        >
          <Sparkles size={15} style={{ marginRight: '6px' }} />
          <span>Why We Started</span>
        </button>

        <button
          type="button"
          className={`tab-pill-btn ${activeSubTab === 'established' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('established')}
        >
          <Calendar size={15} style={{ marginRight: '6px' }} />
          <span>Established Facts</span>
        </button>
      </div>

      {/* SUB-SECTION 1: TIMELINE MILESTONES */}
      {activeSubTab === 'history' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                Chronological Milestones ({formData.historyItems.length})
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
                Major historical events and milestones ordered chronologically by year
              </p>
            </div>
            <button
              type="button"
              className="unified-submit-button"
              onClick={handleAddHistoryItem}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={16} /> Add Milestone
            </button>
          </div>

          {formData.historyItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '14px', border: '2px dashed #cbd5e1' }}>
              <History size={38} color="#94a3b8" style={{ margin: '0 auto 12px' }} />
              <h4 style={{ fontSize: '15px', color: '#334155', margin: '0 0 4px', fontWeight: '700' }}>No Milestones Yet</h4>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Click "Add Milestone" above to record school history</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {formData.historyItems.map((item, idx) => {
                const isPending = pendingHistoryItems[idx];
                const imgSrc = getMediaUrl(item.image);

                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      padding: '18px 24px',
                      borderRadius: '14px',
                      border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                      borderLeft: isPending ? '5px solid #f59e0b' : `5px solid #2563eb`,
                      background: isPending ? '#fffdf7' : '#ffffff',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* Thumbnail */}
                    {imgSrc ? (
                      <div
                        style={{
                          width: '90px',
                          height: '70px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          flexShrink: 0,
                          background: '#0f172a',
                          border: '1px solid #cbd5e1'
                        }}
                      >
                        <img
                          src={imgSrc}
                          alt={item.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => setLightboxImage({ src: imgSrc, title: `${item.year} - ${item.title}` })}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: '8px',
                          background: '#eff6ff',
                          color: '#2563eb',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          fontWeight: '800',
                          fontSize: '15px'
                        }}
                      >
                        {item.year || 'Year'}
                      </div>
                    )}

                    {/* Milestone Details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: '#2563eb',
                            color: '#ffffff',
                            fontSize: '12px',
                            fontWeight: '800'
                          }}
                        >
                          {item.year}
                        </span>
                        <h4 style={{ margin: 0, fontSize: '15.5px', fontWeight: '800', color: '#0f172a' }}>
                          {item.title}
                        </h4>
                        {isPending && (
                          <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                            <Clock size={10} /> Pending Review
                          </span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                        {item.description}
                      </p>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        onClick={() => {
                          if (isPending) {
                            toast.info('This milestone is awaiting approval');
                            return;
                          }
                          setEditingHistoryIndex(idx);
                          setModalHistoryData({ ...item });
                        }}
                        disabled={isPending}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <Edit3 size={14} /> Edit
                      </button>
                      {!isPending && (
                        <button
                          type="button"
                          onClick={() => handleDeleteHistoryItem(idx)}
                          disabled={submitting[`hist_del_${idx}`]}
                          style={{
                            padding: '8px 12px',
                            color: '#ef4444',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                          title="Delete Milestone"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* SUB-SECTION 2: WHY WE STARTED */}
      {activeSubTab === 'whystarted' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                Why We Started Narrative
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
                The founding motivation, community needs, and core purpose behind the school's inception
              </p>
            </div>
            {pendingWhyStarted && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>

          {/* Headline */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Founding Headline</label>
            </div>
            <input
              type="text"
              className="unified-input"
              value={formData.whyStarted.headline}
              onChange={(e) => handleWhyStartedChange('headline', e.target.value)}
              disabled={pendingWhyStarted}
              placeholder="e.g. Born from a Vision of Holistic Transformation"
            />
          </div>

          {/* Paragraph */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Story & Narrative Paragraph</label>
            </div>
            <textarea
              className="unified-textarea"
              rows="5"
              value={formData.whyStarted.paragraph}
              onChange={(e) => handleWhyStartedChange('paragraph', e.target.value)}
              disabled={pendingWhyStarted}
              placeholder="Detailed exposition detailing the circumstances, founder's passion, and educational gaps addressed..."
            />
          </div>

          {/* Motivational Note */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Founding Quote / Note</label>
            </div>
            <textarea
              className="unified-textarea"
              rows="2"
              value={formData.whyStarted.note}
              onChange={(e) => handleWhyStartedChange('note', e.target.value)}
              disabled={pendingWhyStarted}
              placeholder="e.g. Every great journey starts with a single step towards empowering a child's future."
            />
          </div>

          {/* Pillar Tags */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Core Tags / Pillars</label>
            </div>
            
            {/* Existing tag pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
              {(formData.whyStarted.tags || []).map((tag, tIdx) => (
                <span
                  key={tIdx}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    background: '#eff6ff',
                    color: '#1e40af',
                    fontSize: '12.5px',
                    fontWeight: '600',
                    border: '1px solid #bfdbfe'
                  }}
                >
                  <Tag size={12} />
                  <span>{tag}</span>
                  {!pendingWhyStarted && (
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tIdx)}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }}
                    >
                      <X size={13} />
                    </button>
                  )}
                </span>
              ))}
            </div>

            {/* Add tag input */}
            {!pendingWhyStarted && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="unified-input"
                  style={{ maxWidth: '280px' }}
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Enter tag (e.g. Character-First)"
                />
                <button
                  type="button"
                  className="unified-secondary-btn"
                  onClick={handleAddTag}
                  disabled={!newTag.trim()}
                >
                  <Plus size={14} /> Add Tag
                </button>
              </div>
            )}
          </div>

          {/* Submit Why We Started */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
            <button
              type="button"
              className="unified-submit-button"
              onClick={handleSubmitWhyStarted}
              disabled={submitting['why-started'] || pendingWhyStarted}
            >
              <Save size={15} /> {submitting['why-started'] ? 'Submitting...' : 'Save & Submit "Why We Started"'}
            </button>
          </div>
        </div>
      )}

      {/* SUB-SECTION 3: ESTABLISHED FACTS */}
      {activeSubTab === 'established' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                Established History & Facts
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
                Official establishment date, key statistical facts, and closing heritage call-to-action
              </p>
            </div>
            {pendingEstablished && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>

          {/* Established Date */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Established Date / Year</label>
            </div>
            <input
              type="text"
              className="unified-input"
              value={formData.established.date}
              onChange={(e) => handleEstablishedChange('date', e.target.value)}
              disabled={pendingEstablished}
              placeholder="e.g. 2003 or July 2003"
            />
          </div>

          {/* Heritage CTA / Closing */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Closing Heritage Call-to-Action</label>
            </div>
            <textarea
              className="unified-textarea"
              rows="3"
              value={formData.established.cta}
              onChange={(e) => handleEstablishedChange('cta', e.target.value)}
              disabled={pendingEstablished}
              placeholder="e.g. Discover our journey and schedule a campus visit today..."
            />
          </div>

          {/* Facts List */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Institutional Facts & Statistics</label>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
              {(formData.established.facts || []).map((fact, fIdx) => (
                <div
                  key={fIdx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <strong style={{ fontSize: '13px', color: '#0f172a' }}>{fact.label}:</strong>
                    <span style={{ fontSize: '13px', color: '#475569' }}>{fact.value}</span>
                  </div>
                  {!pendingEstablished && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFact(fIdx)}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {!pendingEstablished && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  className="unified-input"
                  style={{ flex: 1, minWidth: '180px' }}
                  value={newFact.label}
                  onChange={(e) => setNewFact(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Fact label (e.g., Campuses)"
                />
                <input
                  type="text"
                  className="unified-input"
                  style={{ flex: 1, minWidth: '180px' }}
                  value={newFact.value}
                  onChange={(e) => setNewFact(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="Fact detail (e.g., 2 Strategic Locations)"
                />
                <button
                  type="button"
                  className="unified-secondary-btn"
                  onClick={handleAddFact}
                  disabled={!newFact.label.trim() || !newFact.value.trim()}
                >
                  <Plus size={14} /> Add Fact
                </button>
              </div>
            )}
          </div>

          {/* Submit Established */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
            <button
              type="button"
              className="unified-submit-button"
              onClick={handleSubmitEstablished}
              disabled={submitting['established'] || pendingEstablished}
            >
              <Save size={15} /> {submitting['established'] ? 'Submitting...' : 'Save & Submit Established Facts'}
            </button>
          </div>
        </div>
      )}

      {/* Edit Milestone Modal */}
      {editingHistoryIndex !== null && modalHistoryData && (
        <div className="unified-modal-overlay" onClick={() => {
          setEditingHistoryIndex(null);
          setModalHistoryData(null);
        }}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                  Edit Historical Milestone
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Update the milestone year, headline, historical narrative, and photography
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingHistoryIndex(null);
                  setModalHistoryData(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              {/* Year */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Milestone Year / Date</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalHistoryData.year || ''}
                  onChange={(e) => handleHistoryModalChange('year', e.target.value)}
                  placeholder="e.g. July 2003 or 2003"
                />
              </div>

              {/* Title */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Milestone Headline</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalHistoryData.title || ''}
                  onChange={(e) => handleHistoryModalChange('title', e.target.value)}
                  placeholder="e.g. Preschool Program Simprug"
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '20px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Narrative Description</label>
                <textarea
                  className="unified-textarea"
                  rows="4"
                  value={modalHistoryData.description || ''}
                  onChange={(e) => handleHistoryModalChange('description', e.target.value)}
                  placeholder="Describe the milestone event, achievements, or expansion..."
                />
              </div>

              {/* Image Upload */}
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Milestone Photo</label>
                {modalHistoryData.image && getMediaUrl(modalHistoryData.image) && (
                  <div style={{ width: '140px', height: '90px', borderRadius: '8px', overflow: 'hidden', marginBottom: '12px', border: '1px solid #cbd5e1' }}>
                    <img
                      src={getMediaUrl(modalHistoryData.image)}
                      alt="Milestone preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                )}
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="file"
                    id="history-image-upload"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                  />
                  <label htmlFor="history-image-upload" className="unified-secondary-btn" style={{ cursor: 'pointer' }}>
                    <Upload size={14} /> {imageUploading ? `Uploading ${imageProgress}%...` : (modalHistoryData.image ? 'Replace Milestone Image' : 'Upload Milestone Image')}
                  </label>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={() => {
                  setEditingHistoryIndex(null);
                  setModalHistoryData(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitHistoryItemModal}
                disabled={submitting[`item_${editingHistoryIndex}`] || !modalHistoryData.year || !modalHistoryData.title || imageUploading}
              >
                <Save size={14} /> {submitting[`item_${editingHistoryIndex}`] ? 'Submitting...' : 'Save & Submit Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                cursor: 'pointer'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title || 'Milestone photo'}
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', background: '#fff' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
